import {
  getExtensionId,
  getCookies,
  createWindow,
  waitOnRemoveWindow,
  removeWindow,
  webRequest,
  checkExtensionVersion,
  type Cookie,
} from ".";

class NotLoggedInError extends Error {
  response: unknown;

  constructor(response: unknown) {
    super(
      "There was an error, please log out from Depop and log into it again."
    );
    this.response = response;
  }
}

export async function getDepopCookies(extensionId: string): Promise<Cookie[]> {
  return await getCookies(extensionId, {
    domain: ".depop.com",
  });
}

export const RequiredExtensionVersion = "1.0.6";
export const DepopReferer = "https://www.depop.com/";

export function checkDepopRequiredExtensionVersion() {
  return checkExtensionVersion(RequiredExtensionVersion);
}

const RequiredCookies = ["user_id", "access_token"];

function cookiesForStorage(cookies: Cookie[]): Cookie[] {
  return cookies.filter((c) => c.name && c.value);
}

function isLoggedInDepop(cookies: Cookie[]): boolean {
  return RequiredCookies.every((name) =>
    cookies.some((c) => c.name === name && c.value)
  );
}

export function getUserIdFromCookies(cookies: Cookie[]): string | null {
  const userId = cookies.find((c) => c.name === "user_id")?.value;
  return userId || null;
}

function getAccessTokenFromCookies(cookies: Cookie[]): string | null {
  const accessToken = cookies.find((c) => c.name === "access_token")?.value;
  return accessToken || null;
}

async function fetchCurrentUser(extensionId: string): Promise<{
  username: string;
  userId: string;
} | null> {
  const cookies = await getDepopCookies(extensionId);
  const accessToken = getAccessTokenFromCookies(cookies);
  const cookieUserId = getUserIdFromCookies(cookies);
  if (!accessToken || !cookieUserId) {
    throw new NotLoggedInError({ cookies });
  }

  const response = await webRequest(extensionId, {
    url: "https://webapi.depop.com/presentation/api/v1/users/me/",
    init: {
      method: "GET",
      credentials: "omit",
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    },
  });

  const data = response?.result?.data;
  const status = response?.result?.status;
  if (status >= 200 && status < 300 && data) {
    const userId =
      data?.id != null
        ? String(data.id)
        : data?.user_id != null
        ? String(data.user_id)
        : "";
    const username = data?.username != null ? String(data.username) : "";

    return {
      username,
      userId: userId || cookieUserId,
    };
  }

  throw new NotLoggedInError(response);
}

async function performDepopLogin(
  extensionId: string
): Promise<Cookie[] | null> {
  const windowsCreateResponse = await createWindow(extensionId, {
    focused: true,
    type: "popup",
    url: "https://www.depop.com/",
  });

  const windowId = windowsCreateResponse.result;
  let closed = false;

  await Promise.any([
    waitOnRemoveWindow(extensionId, { windowId }),
    new Promise<void>((resolve) => {
      const check = async () => {
        if (closed) return resolve();
        const cookies = await getDepopCookies(extensionId);

        if (isLoggedInDepop(cookies)) {
          removeWindow(extensionId, { windowId });
          return resolve();
        }

        setTimeout(() => check(), 1000);
      };

      check();
    }),
  ]);
  closed = true;

  const cookies = await getDepopCookies(extensionId);
  if (isLoggedInDepop(cookies)) {
    return cookiesForStorage(cookies);
  }

  return null;
}

export async function getDepopUser(): Promise<{
  username: string;
  userId: string;
  cookies: Cookie[];
}> {
  checkDepopRequiredExtensionVersion();

  const extensionId = getExtensionId();
  let cookies = await getDepopCookies(extensionId);

  if (!isLoggedInDepop(cookies)) {
    await performDepopLogin(extensionId);
    cookies = await getDepopCookies(extensionId);

    if (!isLoggedInDepop(cookies)) {
      throw new Error("Log into Depop to allow My Reseller Genie to connect.");
    }
  }

  let username = "";
  let userId = getUserIdFromCookies(cookies) || "";

  try {
    const user = await fetchCurrentUser(extensionId);
    if (user) {
      username = user.username;
      userId = user.userId || userId;
    }
  } catch (e) {
    if (!(e instanceof NotLoggedInError)) throw e;
  }

  if (!userId) {
    throw new Error(
      "Unable to detect your Depop user ID. Please log out of Depop and log back in, then try again."
    );
  }

  if (!username) {
    throw new Error(
      "Unable to detect your Depop username. Please log out of Depop and log back in, then try again."
    );
  }

  return {
    username,
    userId,
    cookies: cookiesForStorage(cookies),
  };
}
